/**
 * Ottav version drift: every cell that differs between the 61075 workbook and
 * the latest edition (changed values, rows added or removed).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { styles, StatCard, Spinner, ErrorBox, fmtHours, fmtInt, apiGet, errMsg } from './rbShared';

const CHANGE_STYLE = {
  changed: ['#dbeafe', '#1e40af'],
  added_in_latest: ['#dcfce7', '#166534'],
  removed_in_latest: ['#fee2e2', '#991b1b'],
};

export default function OttavDiff() {
  const { instance, accounts } = useMsal();
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setError(null);
    apiGet(instance, accounts, '/rate-benchmarks/ottav-diff')
      .then(setDiff)
      .catch((e) => setError(errMsg(e)));
  }, [instance, accounts]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!diff) return [];
    let out = diff.data || [];
    if (typeFilter) out = out.filter((r) => r.change_type === typeFilter);
    if (search) {
      const s = search.toLowerCase();
      out = out.filter((r) =>
        String(r.description || '').toLowerCase().includes(s)
        || String(r.material || '').toLowerCase().includes(s)
        || String(r.category || '').toLowerCase().includes(s));
    }
    return out;
  }, [diff, typeFilter, search]);

  if (error) return <ErrorBox error={error} onRetry={load} />;
  if (!diff) return <Spinner label="Comparing Ottav versions…" />;

  const s = diff.summary || {};
  return (
    <div>
      <div style={styles.statGrid}>
        <StatCard label="Changed values" value={fmtInt(s.changed)} color="#1e40af" />
        <StatCard label="Added in latest" value={fmtInt(s.added_in_latest)} color="#166534" />
        <StatCard label="Removed in latest" value={fmtInt(s.removed_in_latest)} color="#991b1b" />
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input
          style={{ ...styles.input, width: 220 }}
          placeholder="Search description / material…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All change types</option>
          <option value="changed">Changed</option>
          <option value="added_in_latest">Added in latest</option>
          <option value="removed_in_latest">Removed in latest</option>
        </select>
        <span style={{ alignSelf: 'center', fontSize: '0.78rem', color: '#6b7280' }}>{fmtInt(rows.length)} rows</span>
      </div>
      <div style={styles.scroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Material</th>
              <th style={styles.th}>Sch/Rating</th>
              <th style={styles.th}>Size</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>61075</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Latest</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Δ</th>
              <th style={styles.th}>Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 2000).map((r, i) => {
              const [bg, fg] = CHANGE_STYLE[r.change_type] || ['#f3f4f6', '#374151'];
              const delta = r.hours_61075 !== null && r.hours_latest !== null
                ? r.hours_latest - r.hours_61075 : null;
              return (
                <tr key={i}>
                  <td style={styles.td}>{r.category}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{r.description}</td>
                  <td style={styles.td}>{r.material}</td>
                  <td style={styles.td}>{r.sch_rating}</td>
                  <td style={styles.td}>{r.size}</td>
                  <td style={styles.tdNum}>{fmtHours(r.hours_61075)}</td>
                  <td style={styles.tdNum}>{fmtHours(r.hours_latest)}</td>
                  <td style={{ ...styles.tdNum, color: delta > 0 ? '#991b1b' : '#166534', fontWeight: 600 }}>
                    {delta === null ? '' : (delta > 0 ? '+' : '') + fmtHours(delta)}
                  </td>
                  <td style={styles.td}><span style={styles.badge(bg, fg)}>{r.change_type.replace(/_/g, ' ')}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 2000 && (
        <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#9ca3af' }}>Showing first 2,000 rows — narrow with the filters above.</div>
      )}
    </div>
  );
}
